import React from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Emphasis,
  Heading,
  List,
  ListItem,
  Strong,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Text,
  TextLink,
} from "@origin-digital/ods-core";
import { MarkdownBlocks, MarkdownOrderedList } from "./AIAnswer.styles";

/**
 * Squiz returns the conversational answer as basic markdown — bullet and
 * numbered lists, bold, italic, links and headings.
 *
 * Every node is mapped onto an ODS component so the answer inherits Origin
 * typography, spacing and colour rather than raw browser defaults. Anything
 * left unmapped renders as a bare HTML element, which is why the tables below
 * are mapped too: they are out of the agreed vendor scope, but if one ever
 * arrives an unstyled `<table>` in the middle of the page is a worse failure
 * than a styled one.
 *
 * Raw HTML in the answer is NOT rendered — `rehype-raw` is deliberately not
 * installed, so react-markdown escapes any HTML it finds. Combined with
 * react-markdown's default `urlTransform` (which strips `javascript:` and
 * other unsafe schemes from links) this keeps untrusted upstream text inert
 * without a separate sanitiser. Markdown image syntax is dropped for the same
 * reason: a rendered `<img>` would reach out to an upstream-supplied host on
 * paint, which is a network request we are not willing to make on the
 * visitor's behalf.
 */

/**
 * react-markdown passes the newlines between list items through as whitespace
 * text nodes. ODS List is a Stack underneath, and Stack wraps *every* child in
 * its own `<li>` — including those whitespace nodes, which then render as empty
 * bulleted rows. Dropping them leaves only the real items.
 */
const withoutWhitespaceNodes = (children: React.ReactNode): React.ReactNode[] =>
  React.Children.toArray(children).filter(
    (child) => typeof child !== "string" || child.trim() !== ""
  );

const components: Components = {
  p: ({ children }) => (
    <Text variant="body" tone="neutralDark" weight="regular">
      {children}
    </Text>
  ),

  ul: ({ children }) => (
    <List type="disc" space="small">
      {withoutWhitespaceNodes(children)}
    </List>
  ),
  // `start` is carried through so a list written as `3.` is numbered from 3
  // rather than silently restarting at 1. The value seeds the ODS counter
  // because ODS renders the digits as generated content, not as browser list
  // markers — an `<ol start>` attribute would have no visible effect.
  ol: ({ children, start }) => (
    <MarkdownOrderedList type="decimal" space="small" $start={start ?? 1}>
      {withoutWhitespaceNodes(children)}
    </MarkdownOrderedList>
  ),
  li: ({ children }) => <ListItem>{children}</ListItem>,

  strong: ({ children }) => <Strong>{children}</Strong>,
  em: ({ children }) => <Emphasis>{children}</Emphasis>,

  // The upstream answer is not our content, so treat every link as external:
  // open in a new tab and sever the opener reference.
  a: ({ href, children }) => (
    <TextLink
      href={href ?? ""}
      target="_blank"
      domProps={{ rel: "noopener noreferrer" }}
    >
      {children}
    </TextLink>
  ),

  // Images are outside the agreed vendor scope, but an <img> is not inert the
  // way an unstyled <table> is: the browser contacts the supplied host the
  // moment the answer paints, with no user interaction, leaking visitor
  // network timing and referrer information to whoever the upstream named.
  // The alt text is the only part that carries meaning for the reader, so it
  // is kept and the request is never made.
  img: ({ alt }) => <>{alt ?? ""}</>,

  // The panel's own "AI Overview" title is an h4, so answer headings render at
  // the same visual scale but sit below it in the document outline.
  h1: ({ children }) => (
    <Heading variant="h4" component="h5">
      {children}
    </Heading>
  ),
  h2: ({ children }) => (
    <Heading variant="h4" component="h5">
      {children}
    </Heading>
  ),
  h3: ({ children }) => (
    <Heading variant="h4" component="h6">
      {children}
    </Heading>
  ),
  h4: ({ children }) => (
    <Heading variant="h4" component="h6">
      {children}
    </Heading>
  ),

  table: ({ children }) => <Table>{children}</Table>,
  thead: ({ children }) => <TableHead>{children}</TableHead>,
  tbody: ({ children }) => <TableBody>{children}</TableBody>,
  tr: ({ children }) => <TableRow>{children}</TableRow>,
  th: ({ children }) => <TableCell>{children}</TableCell>,
  td: ({ children }) => <TableCell>{children}</TableCell>,
};

export interface AIAnswerMarkdownProps {
  children: string;
}

export const AIAnswerMarkdown: React.FC<AIAnswerMarkdownProps> = ({
  children,
}) => (
  <MarkdownBlocks data-id="ai-answer-markdown">
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
      {children}
    </ReactMarkdown>
  </MarkdownBlocks>
);
